import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Radio, Select } from 'antd';
import { fetchMedications } from '../api/medicationsApi';
import styles from './MedicationFilterSelect.module.scss';

// Nacin poklapanja izabranih lekova (API param `medicationMatch`):
//   - 'any'   : "Sadrzi izabrane lekove" - terapija ima BAR JEDAN od izabranih (uz bilo koje druge)
//   - 'exact' : "Tacno podudaranje" - terapija ima TACNO izabrane lekove, nijedan vise
export const MEDICATION_MATCH_ANY = 'any';
export const MEDICATION_MATCH_EXACT = 'exact';
export const DEFAULT_MEDICATION_MATCH = MEDICATION_MATCH_ANY;

const MATCH_OPTIONS = [
  { value: MEDICATION_MATCH_ANY, label: 'Sadrži izabrane lekove' },
  { value: MEDICATION_MATCH_EXACT, label: 'Tačno podudaranje' },
];

// Pretraga u listi ne zavisi od velikih slova ni od dijakritika ("cik" nalazi
// i "Čik..."), jer se nazivi lekova kucaju na razne nacine.
const normalizeForSearch = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();

const filterMedicationOption = (input, option) =>
  normalizeForSearch(option?.label).includes(normalizeForSearch(input));

// Multiselect filter "Lek" za liste terapija: nudi nazive iz sifarnika lekova
// (isti izvor kao Select u modalima terapija), a vraca niz izabranih naziva.
// Na vrhu padajuce liste bira se nacin poklapanja (vidi MATCH_OPTIONS).
export default function MedicationFilterSelect({
  value,
  onChange,
  match = DEFAULT_MEDICATION_MATCH,
  onMatchChange,
  placeholder = 'Lek',
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadMedications = async () => {
      setLoading(true);

      const { data, ok, message } = await fetchMedications({
        page: 1,
        limit: 10000,
      });

      if (cancelled) return;

      setLoading(false);

      if (!ok) {
        setOptions([]);
        setLoadError(message || 'Doslo je do greske pri dobavljanju lekova.');
        return;
      }

      const names = Array.isArray(data?.medications)
        ? data.medications
            .map((medication) => medication?.name?.trim())
            .filter(Boolean)
        : [];

      setLoadError('');
      setOptions(
        [...new Set(names)]
          .sort((left, right) => left.localeCompare(right, 'sr-Latn'))
          .map((name) => ({ value: name, label: name }))
      );
    };

    loadMedications();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch={{ filterOption: filterMedicationOption }}
      // Nazivi lekova su dugacki za polje filtera, pa se umesto tagova prikazuje
      // sazetak kao kod ostalih filtera: jedan lek -> njegov naziv, vise -> "Lek (N)".
      // Kod "Tacno podudaranje" to se vidi i na zatvorenom polju.
      maxTagCount={0}
      maxTagPlaceholder={(omittedValues) =>
        `${
          omittedValues.length === 1
            ? omittedValues[0]?.label
            : `${placeholder} (${omittedValues.length})`
        }${match === MEDICATION_MATCH_EXACT ? ' · tačno' : ''}`
      }
      popupRender={(menu) => (
        <>
          {/* preventDefault: klik na radio ne sme da skine fokus sa Select-a
              (inace se lista zatvori pri promeni nacina). */}
          <div
            className={styles.matchMode}
            role="presentation"
            onMouseDown={(event) => event.preventDefault()}
          >
            <Radio.Group
              vertical
              size="small"
              value={match}
              onChange={(event) => onMatchChange?.(event.target.value)}
              options={MATCH_OPTIONS}
            />
          </div>
          {menu}
        </>
      )}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      notFoundContent={
        loading ? 'Ucitavanje...' : loadError || 'Nema lekova za prikaz'
      }
      popupMatchSelectWidth={320}
      style={{ width: '100%' }}
    />
  );
}

MedicationFilterSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  match: PropTypes.oneOf([MEDICATION_MATCH_ANY, MEDICATION_MATCH_EXACT]),
  onMatchChange: PropTypes.func,
  placeholder: PropTypes.string,
};
